import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Edit2, Trash2, ClipboardCheck, Link as LinkIcon, FileText } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { formatCurrency, getBookingPriceBreakdown } from '../../lib/cleaningPricing';

const STATUS_COLORS = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  confirmed: 'bg-blue-100 text-blue-800 border-blue-300',
  in_progress: 'bg-purple-100 text-purple-800 border-purple-300',
  completed: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled: 'bg-red-100 text-red-800 border-red-300',
};

const HighlightedText = ({ text, query }) => {
  // If there's no search query, just return the normal text
  if (!query || !query.trim()) {
    return <span>{text}</span>;
  }

  // Split the text dynamically based on the query (case-insensitive 'gi')
  // The parentheses in the regex keep the matched term in the array
  const parts = String(text || '').split(new RegExp(`(${query})`, 'gi'));

  return (
    <span>
      {parts.map((part, index) => 
        // If the part matches the search query, wrap it in a <mark> tag
        part.toLowerCase() === query.toLowerCase() ? (
          <mark key={index} style={{ backgroundColor: '#fef08a', padding: '0 2px', borderRadius: '2px' }}>
            {part}
          </mark>
        ) : (
          // Otherwise, just return the normal text snippet
          <span key={index}>{part}</span>
        )
      )}
    </span>
  );
};

export default function BookingsTable({ 
  bookings, 
  sortConfig, 
  onSort, 
  onCrewAssignment, 
  onStatusChange, 
  onPaymentStatusChange,
  onView, 
  onChecklist, 
  onEmail, 
  onDelete,
  searchQuery
}) {
  // Get today's date in YYYY-MM-DD format for comparison
  const getTodayStr = () => {
    const d = new Date();
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const [todayStr, setTodayStr] = React.useState(getTodayStr());

  React.useEffect(() => {
    const now = new Date();
    const tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const msToMidnight = tomorrow.getTime() - now.getTime();
    
    let intervalId;
    const timeoutId = setTimeout(() => {
      setTodayStr(getTodayStr());
      intervalId = setInterval(() => setTodayStr(getTodayStr()), 24 * 60 * 60 * 1000);
    }, msToMidnight);

    return () => {
      clearTimeout(timeoutId);
      if (intervalId) clearInterval(intervalId);
    };
  }, []);

  const getSortIcon = (key) => {
    // Basic sort icon implementation (assumed from parent logic, simplified here)
    return null; 
  };

  const getRowClass = (booking) => {
    let baseClass = '';
    
    // Corporate ID overrides everything (Gold)
    if (booking.corporate_id) {
        baseClass = 'bg-amber-50 hover:bg-amber-100 border-l-4 border-l-amber-400';
    } 
    // Commercial (Green)
    else if (booking.service_category === 'commercial' || booking.service_type === 'commercial' || (booking.service_category || '').includes('commercial')) {
        baseClass = 'bg-emerald-50 hover:bg-emerald-100 border-l-4 border-l-emerald-400';
    }
    // Full Detailing (Purple)
    else if (booking.service_type === 'full_detailing') {
        baseClass = 'bg-purple-50 hover:bg-purple-100 border-l-4 border-l-purple-400';
    }
    // Residential (Blue)
    else {
        baseClass = 'bg-blue-50 hover:bg-blue-100 border-l-4 border-l-blue-400';
    }

    // Add pulsing glow if booking is for today
    if (booking.preferred_date === todayStr) {
        baseClass += ' animate-pulse-glow';
    }

    return baseClass;
  };

  const getContentClass = (booking) => {
    return booking.status === 'completed' ? 'opacity-50 grayscale transition-all duration-300' : '';
  };

  const getEffectiveCrewAssignment = (booking) => {
    if (booking.service_type === 'commercial') {
      return booking.crew_assignment || 'both';
    }
    return booking.crew_assignment || '';
  };

  const getCrewTriggerClass = (booking) => {
    const crewAssignment = getEffectiveCrewAssignment(booking);

    if (crewAssignment === 'both') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    if (crewAssignment === 'crew_a') return 'bg-blue-50 text-blue-700 border-blue-200';
    if (crewAssignment === 'crew_b') return 'bg-orange-50 text-orange-700 border-orange-200';
    return 'bg-slate-50 text-slate-600';
  };

  const isCommercialJob = (booking) => booking.service_category === 'commercial' || booking.service_type === 'commercial';

  const isResidentialCorporateBooking = (booking) => !isCommercialJob(booking) && !!booking.corporate_id;

  const getBookingTypeMeta = (booking) => {
    if (isCommercialJob(booking)) {
      return {
        label: 'Commercial',
        badgeClass: 'bg-emerald-100 text-emerald-800 ring-emerald-700/20',
        detail: 'Commercial Track',
      };
    }

    if (booking.service_type === 'full_detailing') {
      return {
        label: 'Full Detailing',
        badgeClass: 'bg-purple-100 text-purple-800 ring-purple-700/20',
        detail: 'Full Detailing Track',
      };
    }

    if (isResidentialCorporateBooking(booking)) {
      return {
        label: 'Platinum (Corp ID)',
        badgeClass: 'bg-gradient-to-r from-blue-50 to-amber-50 text-slate-800 ring-amber-300/70',
        detail: `Corp ID ${booking.corporate_id}`,
      };
    }

    return {
      label: 'Platinum Package',
      badgeClass: 'bg-blue-100 text-blue-800 ring-blue-700/20',
      detail: 'Platinum Track',
    };
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-left border-collapse">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-200">
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('created_at')}>
              ID
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('date')}>
              Date
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('customer')}>
              Customer
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm">Booking Type</th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('service')}>
              Service
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('vehicle')}>
              Vehicle
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm">Referral</th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm">Crew</th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('price')}>
              Price
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm cursor-pointer hover:bg-slate-100" onClick={() => onSort('status')}>
              Status
            </th>
            <th className="px-4 py-3 font-semibold text-slate-700 text-sm text-right">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {bookings.map((booking) => {
            const { basePrice, totalWithTax } = getBookingPriceBreakdown(booking);
            const bookingTypeMeta = getBookingTypeMeta(booking);
            return (
            <tr key={booking.id} className={`${getRowClass(booking)} transition-colors border-b border-slate-100`}>
              <td className="px-4 py-3 align-top">
                <div className={`flex flex-col ${getContentClass(booking)}`}>
                  {/* INVOICE ID LINK */}
                  {booking.invoice_id ? (
                    <Link 
                      to={`/cleaning/invoices/${booking.invoice_id}`}
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="font-mono text-sm font-bold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 mb-1"
                      title="View Invoice Detail"
                    >
                      <FileText className="w-3 h-3" />
                      #<HighlightedText text={String(booking.invoice_id)} query={searchQuery} />
                    </Link>
                  ) : (
                    <span className="font-mono text-xs text-slate-400 mb-1">Pending</span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={`flex flex-col ${getContentClass(booking)}`}>
                  <span className="text-xs font-medium text-slate-900">{booking.preferred_date}</span>
                  <span className="text-xs text-slate-500">{booking.preferred_time}</span>
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={getContentClass(booking)}>
                  <p className="font-medium text-slate-900"><HighlightedText text={booking.customer?.full_name} query={searchQuery} /></p>
                  <p className="text-xs text-slate-500"><HighlightedText text={booking.customer?.phone} query={searchQuery} /></p>
                  <p className="text-xs text-slate-500"><HighlightedText text={booking.customer?.email} query={searchQuery} /></p>
                  {booking.service_address && (
                    <p className="text-xs text-slate-400 mt-1 truncate max-w-[150px]" title={booking.service_address.street}>
                      <HighlightedText text={booking.service_address.street} query={searchQuery} />, <HighlightedText text={booking.service_address.city} query={searchQuery} />
                    </p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={`space-y-1 ${getContentClass(booking)}`}>
                  <span
                    className={`inline-flex items-center rounded-md px-2.5 py-1 text-xs font-semibold ring-1 ring-inset ${bookingTypeMeta.badgeClass}`}
                    data-testid={`booking-type-badge-${booking.id}`}
                  >
                    {bookingTypeMeta.label}
                  </span>
                  <p className="text-[11px] text-slate-500" data-testid={`booking-type-detail-${booking.id}`}>
                    {bookingTypeMeta.detail}
                  </p>
                  {booking.created_by && (
                    <p className="text-[10px] text-slate-400 italic mt-1" title={`Created by ${booking.created_by}`}>
                      by {booking.created_by.split('@')[0]}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 align-top relative">
                  {booking.status === 'completed' && (
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50 pointer-events-none">
                      <span className={`text-6xl font-black uppercase tracking-widest border-8 px-6 py-2 -rotate-12 whitespace-nowrap mask-image-grunge ${
                        (booking.payment_status === 'pending' || !booking.payment_status) 
                          ? 'text-red-700 border-red-700 opacity-80 mix-blend-multiply' 
                          : 'text-green-700 border-green-700 opacity-80 mix-blend-multiply'
                      }`}>
                        COMPLETED
                      </span>
                    </div>
                  )}
                <div className={getContentClass(booking)}>
                  <p className="text-sm text-slate-700"><HighlightedText text={booking.service_name} query={searchQuery} /></p>
                  <p className="text-xs text-slate-500"><HighlightedText text={booking.vehicle_size_label} query={searchQuery} /></p>
                  {isCommercialJob(booking) && (
                    <span className="mt-1 inline-flex items-center rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700" data-testid={`booking-track-pill-${booking.id}`}>
                      Commercial track
                    </span>
                  )}
                  {booking.is_second_booking && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 mt-1 text-[10px] font-bold bg-rose-100 text-rose-700 rounded-full border border-rose-200" title="This is a concurrent booking for the same time slot">
                      <span className="w-3.5 h-3.5 flex items-center justify-center bg-rose-200 rounded-full text-[9px]">2</span>
                      Concurrent
                    </span>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={getContentClass(booking)}>
                  <p className="text-sm text-slate-700">{booking.vehicle?.year} {booking.vehicle?.make}</p>
                  <p className="text-xs text-slate-500">{booking.vehicle?.model} - {booking.vehicle?.color}</p>
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={getContentClass(booking)}>
                  <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10 truncate max-w-[120px]" title={booking.referral_source}>
                    {booking.referral_source || 'N/A'}
                  </span>
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={getContentClass(booking)}>
                  <Select
                    value={getEffectiveCrewAssignment(booking)}
                    onValueChange={(val) => onCrewAssignment(booking.id, val)}
                  >
                    <SelectTrigger
                      className={`h-8 text-xs w-32 ${getCrewTriggerClass(booking)}`}
                      data-testid={`booking-row-crew-select-${booking.id}`}
                    >
                      <SelectValue placeholder={booking.service_type === 'commercial' ? 'Both Crews' : 'Unassigned'} />
                    </SelectTrigger>
                    <SelectContent>
                      {booking.service_type === 'commercial' && <SelectItem value="both">Both Crews (A & B)</SelectItem>}
                      <SelectItem value="crew_a">Crew A</SelectItem>
                      <SelectItem value="crew_b">Crew B</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={getContentClass(booking)}>
                  <p className="font-semibold text-emerald-600">${formatCurrency(basePrice)}</p>
                  <p className="mb-1 text-[10px] text-slate-500" data-testid={`booking-total-with-tax-${booking.id}`}>Total w/ tax: ${formatCurrency(totalWithTax)}</p>
                  <Select
                    value={booking.payment_status || 'pending'}
                    onValueChange={(val) => onPaymentStatusChange && onPaymentStatusChange(booking.id, val)}
                  >
                    <SelectTrigger className={`h-6 text-[10px] w-24 uppercase font-bold px-1.5 py-0.5 rounded ${
                      booking.payment_status === 'corporate_billing'
                        ? 'bg-indigo-100 text-indigo-700 border-indigo-200'
                        : (booking.payment_status && booking.payment_status.startsWith('paid'))
                          ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
                          : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}>
                      <SelectValue placeholder="STATUS" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">PENDING</SelectItem>
                      <SelectItem value="corporate_billing">CORPORATE BILLING</SelectItem>
                      <SelectItem value="paid">PAID (General)</SelectItem>
                      <SelectItem value="paid-zelle">PAID (Zelle)</SelectItem>
                      <SelectItem value="paid-check">PAID (Check)</SelectItem>
                      <SelectItem value="paid-card">PAID (Card)</SelectItem>
                      <SelectItem value="paid-cash">PAID (Cash)</SelectItem>
                    </SelectContent>
                  </Select>
                  {booking.corporate_id && (
                    <p className="mt-1 text-[10px] font-semibold text-indigo-600" data-testid={`booking-corporate-id-${booking.id}`}>
                      Corp ID: {booking.corporate_id}
                    </p>
                  )}
                </div>
              </td>
              <td className="px-4 py-3 align-top">
                <div className={getContentClass(booking)}>
                  <Select
                    value={booking.status}
                    onValueChange={(val) => onStatusChange(booking.id, val)}
                  >
                    <SelectTrigger className={`h-8 text-xs w-28 ${STATUS_COLORS[booking.status]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </td>
              <td className="px-4 py-3 align-top text-right">
                <div className={`flex items-center justify-end gap-1 ${getContentClass(booking)}`}>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onChecklist(booking)} title="Checklist">
                    <ClipboardCheck className="w-4 h-4 text-slate-500 hover:text-emerald-600" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onEmail(booking)} title="Email Customer">
                    <Mail className="w-4 h-4 text-slate-500 hover:text-blue-600" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => onView(booking)} title="Edit Booking">
                    <Edit2 className="w-4 h-4 text-slate-500 hover:text-amber-600" />
                  </Button>
                  <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => onDelete(booking)} title="Delete">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
